/**
 * Assembly bay — what the main factory is building right now.
 *
 * Ordering a drone used to be an instant purchase with no visible consequence.
 * Now it occupies the one bay for real ticks, so there has to be somewhere that
 * says what is in it and what is waiting, or the player has ordered a heavy
 * drone and simply cannot tell.
 */

import { ASSEMBLY_QUEUE_LIMIT } from '../../sim/constants';
import { DRONE_CLASSES } from '../../sim/data';
import { assemblyTicks } from '../../sim/units';
import type { AssemblyOrder, GameState } from '../../sim/types';
import { el, section, setText, type Issue, type Panel } from './panel';

const VISIBLE = ASSEMBLY_QUEUE_LIMIT;

function describe(order: AssemblyOrder): string {
  return order.unit === 'mech' ? 'mech' : `${order.class ?? 'standard'} drone`;
}

export function createAssemblyPanel(issue: Issue): Panel {
  const { root, body } = section('Assembly bay');

  const current = el('div', 'power-headline');
  const barTrack = el('div', 'bar-track');
  const barFill = el('div', 'bar-fill');
  barTrack.append(barFill);

  const hint = el('div', 'hint');

  const order = el('div', 'button-row');
  for (const droneClass of DRONE_CLASSES) {
    const button = el('button', 'action', `+ ${droneClass}`);
    button.title = `${assemblyTicks({ unit: 'drone', class: droneClass })} ticks in the bay`;
    button.addEventListener('click', () => {
      issue({ type: 'buildUnit', unit: 'drone', class: droneClass });
    });
    order.append(button);
  }
  const mech = el('button', 'action', '+ mech');
  mech.title = `${assemblyTicks({ unit: 'mech' })} ticks in the bay`;
  mech.addEventListener('click', () => {
    issue({ type: 'buildUnit', unit: 'mech' });
  });
  order.append(mech);

  const list = el('div', 'queue-list');
  const rows = Array.from({ length: VISIBLE }, (_, index) => {
    const row = el('div', 'queue-row');
    const label = el('span', 'row-label');
    const cancel = el('button', 'action', '×');
    cancel.title = 'Cancel — nothing is spent until an order reaches the bay';
    cancel.addEventListener('click', () => {
      issue({ type: 'cancelAssembly', index });
    });
    row.append(label, cancel);
    list.append(row);
    return { root: row, label };
  });

  body.append(current, barTrack, hint, order, list);

  return {
    root,
    update(state: GameState): void {
      const job = state.factory.assembly;
      const queue = state.factory.assemblyQueue;

      if (job === null) {
        setText(current, 'Bay idle');
        current.className = 'power-headline';
        barFill.style.width = '0%';
        setText(
          hint,
          queue.length > 0
            ? 'Waiting on materials for the next order.'
            : 'The factory builds drones and mechs here, one at a time.',
        );
      } else {
        const done = job.totalTicks - job.ticksRemaining;
        setText(current, `Building ${describe(job)} — ${job.ticksRemaining} ticks left`);
        current.className = 'power-headline';
        barFill.style.width = `${Math.min(100, (done / job.totalTicks) * 100).toFixed(1)}%`;
        setText(hint, `${queue.length} waiting`);
      }

      for (let index = 0; index < VISIBLE; index++) {
        const row = rows[index];
        if (row === undefined) continue;
        const waiting = queue[index];
        if (waiting === undefined) {
          row.root.classList.add('hidden');
          continue;
        }
        row.root.classList.remove('hidden');
        setText(row.label, `${index + 1}. ${describe(waiting)}`);
      }
    },
  };
}
